import React, { JSX, useEffect, useState } from 'react';
import {
	Box,
	Container,
	Heading,
	Text,
	Flex,
	VStack,
	Icon,
	Image,
	Link as ChakraLink,
	SimpleGrid,
	Button,
} from '@chakra-ui/react';
import Link from 'next/link';
import SEO from 'components/seo';
import {
	ArrowLeftIcon,
	ArrowRightIcon,
	CameraIcon,
	CheckIcon,
	ClapperboardIcon,
	ClockIcon,
	CompassIcon,
	CopyIcon,
	FilmIcon,
	QuoteIcon,
	SparklesIcon,
	Wand2Icon,
} from 'lucide-react';
import { POSTS } from 'services/frontend/blogService';
import { CORPORATE, SHARE_VIDEOS, VIDEOS } from 'services/shared/imageService';
import { ROUTES } from 'services/shared/routerService';
import { copyToClipboard } from 'services/shared/helperService';
import { toaster } from 'components/toaster';

function BlogPost(): JSX.Element {
	const post = POSTS.find((p) => p.slug === 'how-to-convert-image-to-video-with-ai')!;
	const related = POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

	return (
		<Box bg="white" minH="100vh">
			<ReadingProgress />

			<ImageToVideoArticle post={post} />

			{/* Related */}
			<Box borderTop="1px solid" borderColor="gray.100" bg="gray.50" py={24}>
				<Container maxW="container.xl">
					<Flex justify="space-between" align="end" mb={10}>
						<Heading
							as="h3"
							fontSize={{ base: '1.5rem', md: '2.4vw', lg: '2rem' }}
							letterSpacing="-0.025em"
						>
							Keep reading
						</Heading>
						<Link href="/blog">
							<Text
								fontSize="12px"
								fontWeight="600"
								textTransform="uppercase"
								letterSpacing="0.18em"
								color="gray.500"
								_hover={{ color: 'realBlack' }}
							>
								All posts →
							</Text>
						</Link>
					</Flex>
					<SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} gap={10}>
						{related.map((p) => (
							<Link key={p.slug} href={`/blog/${p.slug}`}>
								<VStack align="start" gap={0}>
									<Box
										overflow="hidden"
										rounded="2xl"
										border="1px solid"
										borderColor="gray.100"
										w="full"
									>
										<Image
											src={p.cover}
											alt={p.title}
											aspectRatio="4/3"
											w="full"
											objectFit="cover"
											transition="all 0.7s"
											_groupHover={{ transform: 'scale(1.03)' }}
										/>
									</Box>
									<Text
										mt={5}
										fontSize="11px"
										fontWeight="600"
										textTransform="uppercase"
										letterSpacing="0.18em"
										color="gray.500"
									>
										{p.category}
									</Text>
									<Heading
										as="h4"
										mt={2}
										fontSize="20px"
										lineHeight="1.18"
										letterSpacing="-0.02em"
										_groupHover={{ textDecoration: 'underline', textUnderlineOffset: '4px' }}
									>
										{p.title}
									</Heading>
								</VStack>
							</Link>
						))}
					</SimpleGrid>
				</Container>
			</Box>
		</Box>
	);
}

function ReadingProgress() {
	const [p, setP] = useState(0);
	useEffect(() => {
		const onScroll = () => {
			const h = document.documentElement;
			const total = h.scrollHeight - h.clientHeight;
			setP(total > 0 ? (h.scrollTop / total) * 100 : 0);
		};
		onScroll();
		window.addEventListener('scroll', onScroll, { passive: true });
		return () => window.removeEventListener('scroll', onScroll);
	}, []);
	return (
		<Box position="fixed" insetX={0} top={0} zIndex={50} h="2px" bg="transparent">
			<Box h="full" bg="primary" transition="width 0.15s" style={{ width: `${p}%` }} />
		</Box>
	);
}

const TOC = [
	{ id: 'why', label: 'Introduction' },
	{ id: 'source', label: '1 · Start with a photo' },
	{ id: 'move', label: '2 · Pick the camera move before the prompt' },
	{ id: 'prompt', label: '3 · Write the prompt like a shot list' },
	{ id: 'ratio', label: '4 · Match the ratio to the platform' },
	{ id: 'iterate', label: '5 · Treat the first take as a sketch' },
	{ id: 'checklist', label: 'The 60-second checklist' },
];

function ImageToVideoArticle({ post }: { post: (typeof POSTS)[number] }) {
	const [activeId, setActiveId] = useState<string>('');

	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						setActiveId(entry.target.id);
					}
				});
			},
			{ rootMargin: '-10% 0% -70% 0%' }
		);

		TOC.forEach((item) => {
			const el = document.getElementById(item.id);
			if (el) observer.observe(el);
		});

		return () => observer.disconnect();
	}, []);

	return (
		<Box as="article">
			<SEO
				title={`How to Convert Image to Video with AI | Bring your photos to life Online`}
				description={
					'Learn how to generate videos out of photos and images with AI. Try our AI Image to Video Generator Tool Online to add motion to your images and create stunning videos.'
				}
			/>

			{/* Editorial hero - Compact & Premium */}
			<Box
				position="relative"
				overflow="hidden"
				borderBottom="1px solid"
				borderColor="gray.100"
				bg="white"
				pt={{ base: 12, lg: 16 }}
				pb={{ base: 12, lg: 16 }}
			>
				{/* Ambient dots background */}
				<Box
					position="absolute"
					inset={0}
					pointerEvents="none"
					opacity={0.03}
					style={{
						backgroundImage: 'radial-gradient(circle at 1px 1px, #000 1px, transparent 0)',
						backgroundSize: '24px 24px',
					}}
				/>

				<Container maxW="1400px" px={8} position="relative" zIndex={1}>
					<Link href="/blog">
						<Flex
							align="center"
							gap={1.5}
							fontSize="12px"
							fontWeight="600"
							color="gray.400"
							_hover={{ color: 'primary' }}
							mb={8}
							textTransform="uppercase"
							letterSpacing="0.1em"
						>
							<Icon as={ArrowLeftIcon} h={3.5} w={3.5} /> Blog
						</Flex>
					</Link>

					<SimpleGrid columns={{ base: 1, lg: 2 }} gap={16} alignItems="center">
						{/* Left Content */}
						<VStack align="start" gap={6}>
							<Flex align="center" gap={2}>
								<Flex
									align="center"
									gap={1.5}
									rounded="full"
									bg="realBlack"
									px={3}
									py={1}
									fontSize="10px"
									fontWeight="700"
									textTransform="uppercase"
									letterSpacing="0.18em"
									color="realWhite"
								>
									<Icon as={FilmIcon} h={3} w={3} /> {post.category}
								</Flex>
								<Flex
									align="center"
									gap={1.5}
									rounded="full"
									border="1px solid"
									borderColor="gray.100"
									bg="gray.50"
									px={3}
									py={1}
									fontSize="10.5px"
									fontWeight="600"
									color="gray.500"
								>
									<Icon as={ClockIcon} h={3} w={3} /> {post.readTime}
								</Flex>
							</Flex>

							<Heading
								as="h1"
								fontSize={{ base: '40px', md: '56px', lg: '64px' }}
								fontWeight={500}
								lineHeight={1.1}
								letterSpacing="-0.04em"
								color="realBlack"
							>
								How to convert{' '}
								<Text as="span" color="gray.400">
									image
								</Text>{' '}
								to{' '}
								<Text as="span" color="primary">
									video
								</Text>{' '}
								with AI.
							</Heading>

							<Text fontSize="18px" lineHeight="1.6" color="gray.500" maxW="xl">
								{post.excerpt}
							</Text>

							<Flex align="center" gap={3} pt={2}>
								{/*<Text fontSize="13px" fontWeight="700" color="realBlack">*/}
								{/*	{post.author}*/}
								{/*</Text>*/}
								{/*<Box w={1} h={1} rounded="full" bg="gray.300" />*/}
								<Text fontSize="13px" fontWeight="500" color="gray.400">
									Published · {post.date}
								</Text>
							</Flex>
						</VStack>

						{/* Right Content - Cinematic Video Visual */}
						<Box position="relative" maxW="300px" ml={{ base: 'auto', md: 20 }} w="full">
							<Box
								position="absolute"
								inset="-30px"
								zIndex={-1}
								borderRadius="3rem"
								bg="primary"
								opacity={0.1}
								filter="blur(50px)"
								pointerEvents="none"
							/>

							<Box
								position="relative"
								overflow="hidden"
								rounded="32px"
								boxShadow="2xl"
								border="1px solid"
								borderColor="whiteAlpha.200"
								bg="black"
								aspectRatio="9/16"
							>
								<video
									autoPlay
									loop
									muted
									playsInline
									src={SHARE_VIDEOS.vmp4_28114c11_6e66_4c8e_8e5b_53f44f7455f6_hd}
									style={{
										width: '100%',
										height: '100%',
										objectFit: 'cover',
									}}
								/>
							</Box>

							{/* Floating Source Badge */}
							<Box
								position="absolute"
								left="-24px"
								top="40px"
								bg="white"
								p={2}
								borderRadius="2xl"
								shadow="2xl"
								border="1px solid"
								borderColor="gray.100"
								transform="rotate(-6deg)"
								zIndex={2}
							>
								<Box borderRadius="xl" overflow="hidden" position="relative" w="20" h="20">
									<Image
										src={SHARE_VIDEOS.vpng_28114c11_6e66_4c8e_8e5b_53f44f7455f6_hd}
										alt="Product source"
										style={{ objectFit: 'cover' }}
									/>
								</Box>
								<Text
									mt={0.5}
									textAlign="center"
									fontSize="9px"
									fontWeight="700"
									textTransform="uppercase"
									letterSpacing="0.2em"
									color="gray.400"
								>
									Source
								</Text>
							</Box>
						</Box>
					</SimpleGrid>
				</Container>
			</Box>

			{/* Body with TOC */}
			<Container maxW="container.xl" py={20} position="relative">
				<Flex justify="center" position="relative">
					{/* TOC - Sticky on the left */}
					<Box
						as="aside"
						display={{ base: 'none', lg: 'block' }}
						position="absolute"
						left={0}
						top={0}
						h="full"
						w="220px"
					>
						<Box position="sticky" top="120px">
							<Text
								fontSize="10.5px"
								fontWeight="600"
								textTransform="uppercase"
								letterSpacing="0.22em"
								color="gray.500"
							>
								Article
							</Text>
							<VStack
								as="ol"
								mt={5}
								gap={3}
								align="start"
								borderLeft="1px solid"
								borderColor="gray.100"
								pl={4}
							>
								{TOC.map((t, i) => {
									const isActive = activeId === t.id;
									return (
										<Box as="li" key={t.id} listStyleType="none">
											<ChakraLink
												href={`#${t.id}`}
												fontSize="12.5px"
												lineHeight="snug"
												color={isActive ? 'realBlack' : 'gray.500'}
												fontWeight={isActive ? '600' : '400'}
												_hover={{ color: 'realBlack' }}
												transition="all 0.2s"
											>
												<Text
													as="span"
													mr={1.5}
													color={isActive ? 'primary' : 'gray.300'}
													fontVariantNumeric="tabular-nums"
												>
													{String(i + 1).padStart(2, '0')}
												</Text>
												{t.label.replace(/^\d+ · /, '')}
											</ChakraLink>
										</Box>
									);
								})}
							</VStack>
						</Box>
					</Box>

					{/* Main Content - Centered */}
					<Box minW="0" maxW="800px" w="full">
						<Prose>
							{/* Intro with drop cap */}
							<Text
								id="why"
								_first={{
									_firstLetter: {
										float: 'left',
										mr: 3,
										mt: 2.5,
										fontSize: '3.5rem',
										fontWeight: 500,
										lineHeight: 0.85,
										color: 'realBlack',
									},
								}}
							>
								Transform images to short videos adding motion and storytelling. <br />
								Today, static images no longer have to stay still. With AI-powered image-to-video
								technology, you can generate videos out of a single image, creating engaging, dynamic
								clips that capture attention. <br />
								Whether you want to convert photos to videos with AI for social media, marketing, or
								personal projects, the process has never been easier.
							</Text>
							<Text>This is the exact process we use inside imagemotion. No magic, no jargon.</Text>

							{/* Step 1 */}
							<StepHeading
								id="source"
								n={1}
								icon={<Icon as={CameraIcon} h={4} w={4} />}
								title="Start with a photo"
							/>
							<Text>
								AI video models extrapolate motion from the pixels you give them. A tightly cropped
								photo leaves nothing to move into. Before you upload, look for these qualities:
							</Text>
							<VStack as="ul" align="start" gap={2} pl={0}>
								<Box
									as="li"
									position="relative"
									pl={6}
									_before={{
										content: '""',
										position: 'absolute',
										left: 0,
										top: '0.85em',
										h: '3px',
										w: '2.5',
										rounded: 'full',
										bg: 'gray.600',
									}}
								>
									<Text as="strong" color="realBlack">
										Clean separation
									</Text>{' '}
									between subject and background.
								</Box>
								<Box
									as="li"
									position="relative"
									pl={6}
									_before={{
										content: '""',
										position: 'absolute',
										left: 0,
										top: '0.85em',
										h: '3px',
										w: '2.5',
										rounded: 'full',
										bg: 'gray.600',
									}}
								>
									<Text as="strong" color="realBlack">
										Real light direction
									</Text>{' '}
									— even soft window light is fine. Flat phone-flash photos collapse on camera moves.
								</Box>
								<Box
									as="li"
									position="relative"
									pl={6}
									_before={{
										content: '""',
										position: 'absolute',
										left: 0,
										top: '0.85em',
										h: '3px',
										w: '2.5',
										rounded: 'full',
										bg: 'gray.600',
									}}
								>
									<Text as="strong" color="realBlack">
										Honest color
									</Text>{' '}
									Heavy filters confuse the model; the cleaner the source, the more believable the
									output.
								</Box>
								<Box
									as="li"
									position="relative"
									pl={6}
									_before={{
										content: '""',
										position: 'absolute',
										left: 0,
										top: '0.85em',
										h: '3px',
										w: '2.5',
										rounded: 'full',
										bg: 'gray.600',
									}}
								>
									<Text as="strong" color="realBlack">
										Start Frame
									</Text>{' '}
									The model needs to know what matters in the frame. Strong subject focus leads to
									more intentional camera movement and motion generation.
								</Box>
							</VStack>

							<BeforeAfter />

							{/* Step 2 */}
							<StepHeading
								id="move"
								n={2}
								icon={<Icon as={CompassIcon} h={4} w={4} />}
								title="Pick the camera move before the prompt"
							/>
							<Text>
								Most AI video tools let you describe what the camera does. Decide this <em>first</em> —
								it changes everything else.
							</Text>
						</Prose>

						<CameraMoves />

						<Prose>
							{/* Step 3 */}
							<StepHeading
								id="prompt"
								n={3}
								icon={<Icon as={Wand2Icon} h={4} w={4} />}
								title="Write the prompt like a shot list, not an instruction"
							/>
							<Text>
								AI video models reward concrete, sensory language. Skip <em>"make it cool"</em>.
								Describe the scene the way a director would describe a single shot to a DP.
							</Text>
							<Text>A working template:</Text>
						</Prose>

						<PromptCard
							kind="template"
							copyValue="[camera move] on [subject], [lighting], [environment] [mood], [duration]."
							text={
								<Text as="span">
									<Text as="span" color="primary">
										[camera move]
									</Text>{' '}
									on{' '}
									<Text as="span" color="primary">
										[subject]
									</Text>
									,{' '}
									<Text as="span" color="primary">
										[lighting]
									</Text>
									,{' '}
									<Text as="span" color="primary">
										[environment] [mood]
									</Text>
									,{' '}
									<Text as="span" color="primary">
										[duration]
									</Text>
									.
								</Text>
							}
						/>

						<Prose>
							<Text>Example:</Text>
						</Prose>

						<PromptCard
							kind="example"
							text="Slow cinematic push-in on a child astronaut looking out of a spacecraft window, soft cinematic light from the window illuminating the face, with outer space visible beyond the glass, curious and wonder-filled, 6 seconds."
							video={SHARE_VIDEOS.vmp4_19a52e15_e11c_41a2_89ae_6a7949390ff3_hd}
						/>

						<Prose>
							{/* Step 4 */}
							<StepHeading
								id="ratio"
								n={4}
								icon={<Icon as={ClapperboardIcon} h={4} w={4} />}
								title="Match the ratio to the platform, not the source"
							/>
							<Text>
								Don't be afraid of letting the model reframe. A horizontal product shot can become a
								stunning 9:16 vertical when the AI re-imagines the surrounding scene.
							</Text>
						</Prose>

						<RatioRow />

						<Prose>
							{/* Step 5 */}
							<StepHeading
								id="iterate"
								n={5}
								icon={<Icon as={SparklesIcon} h={4} w={4} />}
								title="Treat the first take as a sketch"
							/>
							<Text>
								The first generation tells you what the model <em>wants</em> to do with your image. Use
								it as information. Re-run with a small change — a different lighting word, a shorter
								duration, a more specific camera move — and you'll converge on a hero clip within three
								to five attempts.
							</Text>

							{/* Checklist */}
							<Heading
								as="h3"
								id="checklist"
								mt={16}
								fontSize="20px"
								letterSpacing="-0.02em"
								color="realBlack"
							>
								The 60-second checklist
							</Heading>
						</Prose>

						<Checklist
							items={[
								'Source photo is high-resolution with space around the subject.',
								'You picked one camera move before you started typing.',
								'Prompt names a subject, a light, a mood and a duration.',
								"Aspect ratio matches the channel you're shipping to.",
								'You ran at least three takes before committing.',
							]}
						/>

						<Prose>
							<Heading
								as="h2"
								mt={20}
								fontSize={{ base: '1.5rem', lg: '2rem' }}
								letterSpacing="-0.025em"
								color="realBlack"
							>
								Doing this in imagemotion
							</Heading>
							<Text>
								Inside the studio, drop a product photo into a new project, pick{' '}
								<Text as="strong" color="realBlack">
									Video
								</Text>{' '}
								in the floating generator, choose your ratio and duration, and paste the prompt. The
								same reference image stays locked across every generation. When a clip lands, hit
								download — it's already encoded for the platform you targeted.
							</Text>
						</Prose>

						<Box mt={12} overflow="hidden" rounded="3xl" bg="realBlack" p={1}>
							<Box rounded="2.2xl" bg="realBlack" p={10} color="white">
								<Text
									fontSize="11px"
									fontWeight="600"
									textTransform="uppercase"
									letterSpacing="0.22em"
									color="whiteAlpha.600"
								>
									Try it yourself
								</Text>
								<Heading
									mt={3}
									fontSize={{ base: '1.75rem', md: '3vw', lg: '2.5rem' }}
									lineHeight="1.05"
									letterSpacing="-0.03em"
									color="white"
								>
									Drop a photo. Get a film.
								</Heading>
								<Link href={ROUTES.REGISTER}>
									<Button
										mt={7}
										rounded="full"
										bg="white"
										px={5}
										py={3}
										h="auto"
										fontSize="13.5px"
										fontWeight="600"
										color="realBlack"
										_hover={{ bg: 'whiteAlpha.900' }}
									>
										Open the studio <Icon as={ArrowRightIcon} h={4} w={4} />
									</Button>
								</Link>
							</Box>
						</Box>

						{/* Author footer */}
						<Flex
							mt={16}
							align="center"
							gap={4}
							rounded="2xl"
							border="1px solid"
							borderColor="gray.100"
							bg="gray.50"
							p={5}
						>
							<Flex
								h={12}
								w={12}
								shrink={0}
								align="center"
								justify="center"
								rounded="full"
								bg="gray.900"
								color="white"
							>
								<Text fontSize="16px" fontWeight="600">
									im
								</Text>
							</Flex>
							<Box minW="0">
								<Text fontSize="13.5px" fontWeight="500">
									{post.author}
								</Text>
								<Text fontSize="12.5px" color="gray.500">
									We write about the craft of advertising at the edge of generative tools — from the
									studio in Germany.
								</Text>
							</Box>
						</Flex>
					</Box>
				</Flex>
			</Container>
		</Box>
	);
}

/* ---------- Sub-components ---------- */

function Prose({ children }: { children: React.ReactNode }) {
	return (
		<VStack align="start" gap={5} fontSize="16.5px" lineHeight="1.78" color="gray.700">
			{children}
		</VStack>
	);
}

function StepHeading({ id, n, title, icon }: { id: string; n: number; title: string; icon: React.ReactNode }) {
	return (
		<Flex id={id} align="center" gap={4} mt={16}>
			<Flex
				h={11}
				w={11}
				shrink={0}
				align="center"
				justify="center"
				rounded="2xl"
				bg="realBlack"
				color="realWhite"
			>
				{icon}
			</Flex>
			<Box>
				<Text
					fontSize="10.5px"
					fontWeight="600"
					textTransform="uppercase"
					letterSpacing="0.22em"
					color="gray.500"
				>
					Step {String(n).padStart(2, '0')}
				</Text>
				<Heading
					as="h2"
					mt={1}
					fontSize={{ base: '1.5rem', lg: '2.15rem' }}
					lineHeight="1.05"
					letterSpacing="-0.025em"
					color="realBlack"
				>
					{title}
				</Heading>
			</Box>
		</Flex>
	);
}

function Pullquote({ children }: { children: React.ReactNode }) {
	return (
		<Box as="blockquote" my={14} borderLeft="2px solid" borderColor="realBlack" pl={6}>
			<Icon as={QuoteIcon} h={5} w={5} color="gray.300" />
			<Text
				mt={2}
				fontSize={{ base: '1.5rem', lg: '2.1rem' }}
				fontWeight={500}
				lineHeight="1.15"
				letterSpacing="-0.025em"
				color="gray.600"
			>
				{children}
			</Text>
		</Box>
	);
}

function BeforeAfter() {
	return (
		<Box as="figure" my={12} w="full">
			<SimpleGrid columns={{ base: 1, sm: 2 }} gap={2}>
				<Box
					position="relative"
					overflow="hidden"
					rounded="2xl"
					border="1px solid"
					borderColor="gray.100"
					w={'fit-content'}
				>
					<Image
						src={CORPORATE.PANDA}
						alt="Reference panda image"
						aspectRatio="9/16"
						objectFit="cover"
						h={'100%'}
						w={'100%'}
					/>
					<Box
						position="absolute"
						left={3}
						top={3}
						rounded="full"
						bg="white/90"
						px={2}
						py={0.5}
						fontSize="10px"
						fontWeight="600"
						textTransform="uppercase"
						letterSpacing="wider"
						color="realBlack"
						backdropFilter="blur(4px)"
					>
						Image
					</Box>
				</Box>
				<Box
					position="relative"
					overflow="hidden"
					rounded="2xl"
					border="1px solid"
					borderColor="gray.100"
					w={'fit-content'}
				>
					<video src={VIDEOS.PANDA} style={{ objectFit: 'cover', aspectRatio: '9/16' }} autoPlay loop muted />
					<Box
						position="absolute"
						left={3}
						top={3}
						rounded="full"
						bg="realBlack"
						px={2}
						py={0.5}
						fontSize="10px"
						fontWeight="600"
						textTransform="uppercase"
						letterSpacing="wider"
						color="white"
					>
						Video
					</Box>
				</Box>
			</SimpleGrid>
			<Text as="figcaption" mt={4} fontSize="12.5px" color="gray.500">
				Left: reference image. Right: ai generated video.
			</Text>
		</Box>
	);
}

function CameraMoves() {
	const moves = [
		{ name: 'Slow push-in', note: 'Dramatic. Best for hero products and packaging reveals.' },
		{ name: 'Orbit', note: 'Half-circle. For objects with a good side on every angle.' },
		{ name: 'Parallax dolly', note: 'Sideways slide with foreground depth. Reads expensive.' },
		{ name: 'Static + subject motion', note: 'Camera holds. The steam rises. The model walks.' },
	];
	return (
		<SimpleGrid columns={{ base: 1, sm: 2 }} gap={3} my={12} w="full">
			{moves.map((m, i) => (
				<Box
					key={m.name}
					position="relative"
					overflow="hidden"
					rounded="2xl"
					border="1px solid"
					borderColor="gray.100"
					bg="gray.50"
					p={5}
					transition="all 0.2s"
					_hover={{ borderColor: 'gray.300' }}
				>
					<Text position="absolute" right={5} top={5} fontFamily="mono" fontSize="11px" color="gray.400">
						M0{i + 1}
					</Text>
					<Text fontSize="17px" letterSpacing="-0.015em" color="realBlack" fontWeight={500}>
						{m.name}
					</Text>
					<Text mt={1.5} fontSize="13px" lineHeight="relaxed" color="gray.500">
						{m.note}
					</Text>
				</Box>
			))}
		</SimpleGrid>
	);
}

function PromptCard({
	kind,
	text,
	video,
	copyValue,
}: {
	kind: 'template' | 'example';
	text: React.ReactNode;
	video?: string;
	copyValue?: string;
}) {
	const handleCopy = () => {
		const valueToCopy = copyValue || (typeof text === 'string' ? text : '');
		if (valueToCopy) {
			copyToClipboard(valueToCopy);
			toaster.create({
				title: 'Copied to clipboard',
				type: 'success',
			});
		}
	};

	return (
		<Box
			as="figure"
			my={10}
			overflow="hidden"
			rounded="3xl"
			border="1px solid"
			borderColor="gray.800"
			bg="realBlack"
			color="white"
			w="full"
		>
			<Flex
				align="center"
				justify="space-between"
				borderBottom="1px solid"
				borderColor="whiteAlpha.100"
				px={5}
				py={3}
			>
				<Text
					fontSize="10.5px"
					fontWeight="600"
					textTransform="uppercase"
					letterSpacing="0.22em"
					color="whiteAlpha.600"
				>
					{kind === 'template' ? 'Prompt template' : 'Working prompt'}
				</Text>
				<Button
					h="auto"
					py={1}
					px={2.5}
					fontSize="11px"
					fontWeight="500"
					bg="whiteAlpha.100"
					color="whiteAlpha.900"
					rounded="full"
					_hover={{ bg: 'whiteAlpha.200' }}
					onClick={handleCopy}
				>
					<Icon as={CopyIcon} h={3} w={3} mr={1.5} /> Copy
				</Button>
			</Flex>
			<SimpleGrid columns={{ base: 1, md: video ? 2 : 1 }} gap={0}>
				<Box
					p={6}
					as="pre"
					whiteSpace="pre-wrap"
					wordBreak="break-word"
					fontFamily="mono"
					fontSize="13.5px"
					lineHeight="1.65"
					color="whiteAlpha.900"
				>
					{text}
				</Box>
				{video && (
					<Box
						position="relative"
						borderLeft={{ base: 'none', md: '1px solid' }}
						borderColor="whiteAlpha.100"
					>
						<video
							src={video}
							style={{ objectFit: 'cover', height: 'full', width: 'full' }}
							autoPlay
							loop
							muted
						/>
					</Box>
				)}
			</SimpleGrid>
		</Box>
	);
}

function RatioRow() {
	const r = [
		{ r: '9:16', w: '34px', h: '60px', note: 'TikTok · Reels · Shorts · Stories' },
		{ r: '1:1', w: '52px', h: '52px', note: 'In-feed Instagram & Facebook' },
		{ r: '4:5', w: '44px', h: '55px', note: 'Feed posts that take real estate' },
		{ r: '16:9', w: '68px', h: '38px', note: 'YouTube pre-roll, in-stream, CTV' },
	];
	return (
		<SimpleGrid columns={{ base: 2, lg: 4 }} gap={3} my={12} w="full">
			{r.map((x) => (
				<Box key={x.r} rounded="2xl" border="1px solid" borderColor="gray.100" bg="gray.50" p={5}>
					<Flex h="72px" align="center" justify="center">
						<Box w={x.w} h={x.h} rounded="md" bg="gray.800" />
					</Flex>
					<Text mt={4} fontFamily="mono" fontSize="12px" color="gray.400">
						{x.r}
					</Text>
					<Text fontSize="13px" lineHeight="snug" color="gray.700">
						{x.note}
					</Text>
				</Box>
			))}
		</SimpleGrid>
	);
}

function Checklist({ items }: { items: string[] }) {
	return (
		<SimpleGrid as="ul" columns={{ base: 1, sm: 2 }} gap={2} my={8} w="full">
			{items.map((i) => (
				<Flex
					as="li"
					key={i}
					align="start"
					gap={3}
					rounded="xl"
					border="1px solid"
					borderColor="gray.100"
					bg="gray.50"
					px={4}
					py={3}
					fontSize="13.5px"
					lineHeight="relaxed"
					color="gray.700"
				>
					<Flex
						mt={0.5}
						h={4}
						w={4}
						shrink={0}
						align="center"
						justify="center"
						rounded="full"
						bg="realBlack"
						color="white"
					>
						<Icon as={CheckIcon} h={2.5} w={2.5} strokeWidth={3} />
					</Flex>
					{i}
				</Flex>
			))}
		</SimpleGrid>
	);
}

export default BlogPost;
